import { type paths } from 'api/playerPayback/api'
import { useAccessTokenContext } from 'contexts/AccessTokenContext'
import {
  Fetcher,
  type OpArgType,
  type OpReturnType,
  type TypedFetch,
} from 'openapi-typescript-fetch'

type HttpMethod =
  | 'get'
  | 'put'
  | 'post'
  | 'delete'
  | 'options'
  | 'head'
  | 'patch'
  | 'trace'

type NormalizedOpArg<OP> = [OpArgType<OP>] extends [never]
  ? unknown
  : [OpArgType<OP>] extends [undefined]
    ? unknown
    : OpArgType<OP>

type NormalizedOpReturn<OP> = [OpReturnType<OP>] extends [never]
  ? unknown
  : OpReturnType<OP>

type LooseTypedFetch<OP> = ((
  arg?: NormalizedOpArg<OP>,
  init?: RequestInit
) => Promise<{ data: NormalizedOpReturn<OP> }>) & {
  Error: TypedFetch<OP>['Error']
}

const getHeaders = (
  disableAuth: boolean,
  token: string,
  headers?: Record<string, string>
) => {
  if (disableAuth) {
    return undefined
  }

  if (headers) {
    return {
      ...headers,
      Authorization: `Bearer ${token}`,
    }
  }

  return {
    Authorization: `Bearer ${token}`,
  }
}

export const useTypedApiClient = <
  PathT extends keyof paths,
  MethodT extends Extract<keyof paths[PathT], HttpMethod>,
>({
  path,
  method,
  disableAuth = false,
  headers,
}: {
  path: PathT
  method: MethodT
  disableAuth?: boolean
  headers?: Record<string, string>
}): LooseTypedFetch<paths[PathT][MethodT]> => {
  const { accessToken } = useAccessTokenContext()
  const fetcher = Fetcher.for<paths>()

  fetcher.configure({
    baseUrl: import.meta.env.VITE_ADMIN_API_BASE_URL,
    init: {
      headers: getHeaders(disableAuth, accessToken ?? '', headers),
    },
  })

  const endpoint = fetcher.path(path).method(method)

  return endpoint.create({
    'SearchNearBy.Latitude': true,
    'SearchNearBy.Longitude': true,
  }) as unknown as LooseTypedFetch<paths[PathT][MethodT]>
}
